/**
 * The chat box (unit 26B; v1's `ChatStrip`). Enter sends, Shift+Enter is a
 * new line, Escape closes the widget in front. While a run is in flight the
 * box still works: commands run at once, and a message joins the run.
 *
 * The palette shows while a command word is typed; after `/note ` or
 * `/todo `, an `@` offers the notebooks or the lists — from the offline
 * cache, so filing works with no signal too.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState, type KeyboardEvent } from "react";

import { api } from "../api/client";
import { ok, reason } from "../api/query";
import { useCached } from "../offline/useOffline";
import { THEMES, usePrefs, type Theme } from "../prefs/store";
import { useRecorder } from "../recorder/store";
import { runEscape } from "../shell/escape";
import { focusedTileIndex } from "../shell/layout";
import { useShownWidgets } from "../shell/shown";
import { useLayout } from "../shell/useLayout";
import type { Target } from "./capture";
import styles from "./ChatStrip.module.css";
import {
  accepted,
  helpText,
  RECORD_COMMANDS,
  route,
  suggest,
  SYSTEM_COMMANDS,
  type CommandInfo,
  type Skill,
} from "./commands";
import { showHistory } from "./history";
import { useIssue } from "./issue";
import { capture } from "./quick";
import { converse } from "./send";
import { useChat } from "./store";

const SKILLS = ["agent", "skills", "enabled"] as const;

/** The `@…` the caret is in, after `/note ` or `/todo ` — spaces and all, as names have them. */
function mention(value: string, caret: number): { kind: "note" | "todo"; fragment: string } | null {
  const command = value.match(/^\/(note|n|todo)\s/i)?.[1]?.toLowerCase();
  if (!command) return null;
  const fragment = value.slice(0, caret).match(/(?:^|\s)@([^@\n]*)$/)?.[1];
  return fragment === undefined ? null : { kind: command === "todo" ? "todo" : "note", fragment };
}

export function ChatStrip() {
  const client = useQueryClient();
  const chat = useChat();
  const draft = chat.draft;
  const box = useRef<HTMLTextAreaElement>(null);
  const [caret, setCaret] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [selected, setSelected] = useState(0);

  const shown = useShownWidgets();
  const widgets = useMemo(() => shown.map((entry) => entry.key).sort(), [shown]);
  const skills = useQuery({
    queryKey: SKILLS,
    queryFn: async () =>
      (
        await ok(api.GET("/api/skills", { params: { query: { include_disabled: false } } }))
      ).skills.map((s): Skill => ({ slug: s.slug, title: s.title, summary: s.summary })),
    staleTime: 5 * 60_000,
  });
  const notebooks = useCached("notebook");
  const lists = useCached("list");

  const commands: CommandInfo[] = useMemo(
    () => [
      ...SYSTEM_COMMANDS,
      ...RECORD_COMMANDS,
      ...widgets.map((name): CommandInfo => ({ name, kind: "widget", desc: `Open ${name}` })),
      ...(skills.data ?? []).map((s): CommandInfo => ({
        name: s.slug,
        kind: "skill",
        desc: s.summary || s.title,
      })),
    ],
    [widgets, skills.data],
  );

  const at = mention(draft, caret);
  const targets: Target[] = (at?.kind === "todo" ? lists : notebooks) ?? [];
  const commandItems = dismissed ? [] : suggest(draft, commands);
  const atItems =
    dismissed || commandItems.length || !at
      ? []
      : targets.filter((t) => t.name.toLowerCase().startsWith(at.fragment.toLowerCase()));
  const atShown =
    atItems.length === 1 && atItems[0]?.name.toLowerCase() === at?.fragment.toLowerCase()
      ? []
      : atItems;
  const menu = commandItems.length ? "command" : atShown.length ? "at" : null;
  const items: (CommandInfo | Target)[] = menu === "command" ? commandItems : atShown;
  const index = Math.min(selected, Math.max(0, items.length - 1));

  const edit = (value: string) => {
    chat.setDraft(value);
    setDismissed(false);
    const node = box.current;
    if (node) {
      node.style.height = "auto";
      node.style.height = `${node.scrollHeight}px`;
    }
  };

  const choose = (item: CommandInfo | Target) => {
    if (menu === "command") {
      edit(accepted(item as CommandInfo));
    } else {
      // Replace the `@…` the caret is in; the space after closes the menu.
      const start = draft.slice(0, caret).lastIndexOf("@");
      const spaced = /^\s/.test(draft.slice(caret));
      const inserted = `@${(item as Target).name}${spaced ? "" : " "}`;
      edit(draft.slice(0, start) + inserted + draft.slice(caret));
      const position = start + inserted.length;
      requestAnimationFrame(() => box.current?.setSelectionRange(position, position));
      setCaret(position);
    }
    setSelected(0);
    box.current?.focus();
  };

  const closeFront = () => {
    // A detail open in a widget closes first (27A).
    if (runEscape()) return;
    const front = focusedTileIndex(useLayout.getState());
    if (front !== null) useLayout.getState().close(front);
  };

  async function submit() {
    const typed = draft;
    const command = route(typed, widgets, skills.data ?? []);
    if (!command) return;
    chat.setDraft("");
    if (box.current) box.current.style.height = "";

    switch (command.kind) {
      case "help":
        chat.say(helpText(widgets, skills.data ?? []));
        return;
      case "clear":
        await clearChat();
        return;
      case "history":
        if (!(await showHistory())) chat.say("Couldn't read the earlier messages.", true);
        return;
      case "theme": {
        const prefs = usePrefs.getState();
        if (command.arg && !(THEMES as readonly string[]).includes(command.arg)) {
          chat.say(
            `No such theme \`${command.arg}\`. Try ${THEMES.map((t) => `\`${t}\``).join(" or ")}.`,
            true,
          );
          return;
        }
        if (command.arg) prefs.setTheme(command.arg as Theme);
        else prefs.toggleTheme();
        chat.say(`Theme set to **${usePrefs.getState().theme}**.`);
        return;
      }
      case "note":
      case "todo": {
        const outcome = await capture(
          command.kind,
          command.text,
          command.kind === "todo" ? (lists ?? []) : (notebooks ?? []),
        );
        chat.say(outcome.reply, outcome.error);
        if (outcome.restore) chat.setDraft(outcome.restore);
        return;
      }
      case "issue": {
        if (!command.text) {
          chat.say("Say what is wrong after it — `/issue the map is blank at z12`.", true);
          return;
        }
        const layout = useLayout.getState();
        const front = layout.tiles[focusedTileIndex(layout) ?? 0];
        useIssue.getState().open({
          report: command.text,
          widget: front?.widget ?? "",
          tab: front?.tab ?? "",
        });
        return;
      }
      case "record":
        useRecorder.getState().start(command.recording);
        return;
      case "widget":
        useLayout.getState().open({ name: command.name });
        return;
      case "sync":
        chat.say("Syncing data from connected sources…");
        try {
          await ok(api.POST("/api/sync"));
        } catch (e) {
          chat.say(`Couldn't start the sync: ${reason(e)}`, true);
        }
        refreshFront();
        return;
      case "message":
        await converse(command.message, command.skill);
        if (command.skill) refreshFront();
        return;
    }
  }

  /** A command may have changed what the widget in front shows: read it again. */
  function refreshFront() {
    const front = useLayout.getState().tiles[focusedTileIndex(useLayout.getState()) ?? 0];
    if (front) void client.invalidateQueries({ queryKey: [front.widget] });
  }

  async function clearChat() {
    try {
      await ok(api.DELETE("/api/messages"));
      useChat.getState().clear();
    } catch (e) {
      useChat.getState().say(`Couldn't clear the conversation: ${reason(e)}`, true);
    }
  }

  async function stop() {
    try {
      await ok(api.POST("/api/chat/stop"));
    } catch {
      // Nothing running: the box settles below.
    }
    useChat.getState().settle();
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (menu && items.length) {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const step = event.key === "ArrowDown" ? 1 : -1;
        setSelected((index + step + items.length) % items.length);
        return;
      }
      if (event.key === "Tab" || (event.key === "Enter" && !event.shiftKey)) {
        event.preventDefault();
        const item = items[index];
        if (item) choose(item);
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        setDismissed(true);
        return;
      }
    }
    if (event.key === "Escape") {
      closeFront();
      return;
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void submit();
    }
  };

  const track = () => setCaret(box.current?.selectionStart ?? draft.length);

  return (
    // `data-shell`: `tools/compare_text.py` leaves the chat box out of a widget's words.
    <div className={styles.strip} data-shell="chat">
      <div className={styles.inner}>
        {chat.lines.length > 0 && (
          <button
            className={styles.clear}
            onClick={() => void clearChat()}
            aria-label="Clear chat"
            title="Clear chat"
          >
            <svg aria-hidden="true" width="13" height="13" viewBox="0 0 14 14" fill="none">
              <path
                d="M2 2l10 10M12 2L2 12"
                stroke="currentColor"
                strokeWidth={1.5}
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
        {menu && (
          <ul
            className={styles.menu}
            role="listbox"
            aria-label={menu === "command" ? "Commands" : "Where to file it"}
          >
            {items.map((item, i) => (
              <li
                key={item.name}
                role="option"
                aria-selected={i === index}
                className={i === index ? styles.selected : undefined}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(item);
                }}
                onMouseEnter={() => setSelected(i)}
              >
                {menu === "command" ? (
                  <>
                    <span className={styles.name}>/{(item as CommandInfo).name}</span>
                    <span className={`${styles.kind} ${styles[(item as CommandInfo).kind] ?? ""}`}>
                      {(item as CommandInfo).kind}
                    </span>
                    <span className={styles.desc}>{(item as CommandInfo).desc}</span>
                  </>
                ) : (
                  <>
                    <span className={styles.name}>@{(item as Target).name}</span>
                    <span className={styles.kind}>{at?.kind === "todo" ? "list" : "notebook"}</span>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
        <textarea
          ref={box}
          value={draft}
          rows={1}
          aria-label="Message"
          placeholder={chat.running ? "Still thinking — commands still work" : ":)"}
          onChange={(e) => {
            edit(e.target.value);
            setCaret(e.target.selectionStart);
          }}
          onKeyDown={onKeyDown}
          onKeyUp={track}
          onClick={track}
        />
        {chat.running && (
          <button
            className={`${styles.button} ${styles.stop}`}
            onClick={() => void stop()}
            aria-label="Stop"
          >
            <svg aria-hidden="true" width="12" height="12" viewBox="0 0 12 12" fill="none">
              <rect x="1" y="1" width="10" height="10" rx="2" fill="currentColor" />
            </svg>
          </button>
        )}
        {(!chat.running || draft.trim()) && (
          <button
            className={styles.button}
            onClick={() => void submit()}
            disabled={!draft.trim()}
            aria-label="Send"
          >
            <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M14 8L2 2l2 6-2 6 12-6z"
                stroke="currentColor"
                strokeWidth={1.5}
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
